/**
 * Phase L-TOOLS-POLISH — Quota-friendly 429 UX.
 *
 * L-AUTH introduced a security rate limit (`services/tool_auth.py`)
 * that returns HTTP 429 with a machine-readable body:
 *
 *   { detail: { error: "QUOTA_EXCEEDED", operation, limit_per_window,
 *               window_seconds, message } }
 *
 * This module installs ONE global axios response interceptor that
 * turns those responses into a single friendly toast. Every axios
 * import in the app shares the module-level default instance, so
 * one install is enough. If a call site catches the error it is
 * still free to add its own tool-specific messaging; the toast
 * fires only once per response.
 *
 * Design constraints (verbatim from the phase brief):
 *   * Use existing toast infrastructure (`sonner`) — do NOT create
 *     a new notification framework.
 *   * Do NOT weaken or bypass the backend quota.
 *   * Do NOT expose internal quota/security implementation details
 *     unnecessarily.
 */
import axios from 'axios';
import { toast } from 'sonner';

let installed = false;

function extractDetail(response) {
  try {
    return response?.data?.detail ?? {};
  } catch {
    return {};
  }
}

function friendlyRetryLine(windowSeconds) {
  if (!Number.isFinite(windowSeconds) || windowSeconds <= 0) return '';
  if (windowSeconds < 60) return `Try again in about ${Math.ceil(windowSeconds)} seconds.`;
  const minutes = Math.ceil(windowSeconds / 60);
  if (minutes <= 1) return 'Try again in about a minute.';
  if (minutes < 60) return `Try again in about ${minutes} minutes.`;
  const hours = Math.ceil(minutes / 60);
  return hours <= 1 ? 'Try again in about an hour.' : `Try again in about ${hours} hours.`;
}

/**
 * Install the interceptor exactly once per app boot. Safe to call
 * multiple times — subsequent invocations are no-ops.
 */
export function installQuotaFriendlyInterceptor() {
  if (installed) return;
  installed = true;

  axios.interceptors.response.use(
    (r) => r,
    (error) => {
      const status = error?.response?.status;
      if (status === 429) {
        const detail = extractDetail(error.response);
        // Only translate quota-exceeded responses. Other 429s (e.g.
        // upstream RPC rate limits, if any) fall through untouched.
        if (detail?.error === 'QUOTA_EXCEEDED') {
          const suffix = friendlyRetryLine(detail?.window_seconds);
          const base = "You're moving fast. Please try again in a few minutes.";
          toast.error(suffix ? `${base} ${suffix}` : base, {
            id: 'quota-exceeded',   // dedupe rapid repeats
            duration: 6000,
          });
        }
      }
      return Promise.reject(error);
    },
  );
}
